import React, { useEffect, useMemo, useState } from "react";
import "./ai-management.scss";
import {
  getAiCredential,
  getAiOverview,
  getAiPrompts,
  testAiCredential,
  testAiPrompt,
  updateAiCredential,
  updateAiPrompt,
} from "../../api/crawlerApi";

const models = ["gpt-4.1-mini", "gpt-4.1", "gpt-4o-mini", "gpt-4o"];
const statuses = ["active", "inactive"];

export default function AIManagementPage() {
  const [credentials, setCredentials] = useState({
    provider: "OpenAI",
    apiKey: "",
    connectionStatus: "pending",
    lastTestedAt: "Not tested yet",
    maskedApiKey: "",
  });
  const [prompts, setPrompts] = useState([]);
  const [tokenUsage, setTokenUsage] = useState([]);
  const [testResult, setTestResult] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    Promise.all([getAiOverview(), getAiCredential(), getAiPrompts()])
      .then(([overview, credential, promptList]) => {
        setCredentials({
          provider: credential.provider || overview.credential?.provider || "OpenAI",
          apiKey: "",
          connectionStatus: credential.connection_status || "pending",
          lastTestedAt: credential.last_tested_at || "Not tested yet",
          maskedApiKey: credential.masked_api_key || "",
        });
        setTokenUsage((overview.token_usage?.daily || []).map((item) => Number(item.total_tokens || item.tokens || 0)));
        setPrompts(promptList.length ? promptList : overview.prompts || []);
      })
      .catch((err) => {
        setError(err.message || "Failed to load AI management data.");
      });
  }, []);

  const maxUsage = tokenUsage.length ? Math.max(...tokenUsage) : 0;
  const totalTokens = tokenUsage.reduce((total, value) => total + value, 0);
  const avgTokens = tokenUsage.length ? Math.round(totalTokens / tokenUsage.length) : 0;

  const promptMap = useMemo(() => prompts, [prompts]);

  const handleCredentialChange = (key, value) => {
    setCredentials((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handlePromptChange = (id, key, value) => {
    setPrompts((prev) =>
      prev.map((prompt) =>
        prompt.id === id ? { ...prompt, [key]: value } : prompt
      )
    );
  };

  const handleTestConnection = async () => {
    try {
      const result = await testAiCredential();
      setCredentials((prev) => ({
        ...prev,
        connectionStatus: result.connection_status || "verified",
        lastTestedAt: result.last_tested_at || new Date().toLocaleString(),
        maskedApiKey: result.masked_api_key || prev.maskedApiKey,
      }));
      setTestResult(JSON.stringify(result, null, 2));
    } catch (err) {
      setError(err.message || "Connection test failed.");
    }
  };

  const handleSaveCredentials = async () => {
    try {
      const result = await updateAiCredential({
        provider: credentials.provider,
        api_key: credentials.apiKey,
      });
      setCredentials((prev) => ({
        ...prev,
        connectionStatus: result.connection_status || prev.connectionStatus,
        lastTestedAt: result.last_tested_at || prev.lastTestedAt,
        maskedApiKey: result.masked_api_key || prev.maskedApiKey,
        apiKey: "",
      }));
    } catch (err) {
      setError(err.message || "Failed to save credentials.");
    }
  };

  const handleSavePrompt = async (prompt) => {
    try {
      await updateAiPrompt(prompt.key, {
        name: prompt.name,
        description: prompt.description,
        model: prompt.model,
        status: prompt.status,
        prompt_template: prompt.prompt_template,
      });
    } catch (err) {
      setError(err.message || "Failed to save prompt.");
    }
  };

  const handleTestPromptResult = async (prompt) => {
    try {
      const result = await testAiPrompt(prompt.key);
      setTestResult(JSON.stringify(result, null, 2));
    } catch (err) {
      setError(err.message || "Prompt test failed.");
    }
  };

  return (
    <div className="ai-management-page">
      <div className="ai-management-page__header">
        <div>
          <h1>AI Management</h1>
          <p>Manage OpenAI credentials, monitor token usage, and configure prompts.</p>
        </div>
      </div>

      {error && <div className="ai-card">{error}</div>}

      <div className="ai-card usage-card">
        <div className="ai-card__header">
          <div>
            <span className="ai-badge">usage</span>
            <h2>Daily Token Usage</h2>
            <p>Last 30 days OpenAI token consumption.</p>
          </div>
        </div>

        <div className="usage-summary">
          <div>
            <label>Total Tokens</label>
            <strong>{totalTokens.toLocaleString()}</strong>
          </div>
          <div>
            <label>Daily Average</label>
            <strong>{avgTokens.toLocaleString()}</strong>
          </div>
          <div>
            <label>Highest Day</label>
            <strong>{maxUsage.toLocaleString()}</strong>
          </div>
        </div>

        <div className="usage-chart" aria-label="Daily token usage chart">
          {tokenUsage.map((value, index) => (
            <div className="usage-chart__bar-wrap" key={index}>
              <div
                className="usage-chart__bar"
                style={{ height: `${maxUsage ? Math.max(10, (value / maxUsage) * 100) : 0}%` }}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="ai-card">
        <div className="ai-card__header">
          <div>
            <span className="ai-badge">credential</span>
            <h2>OpenAI Credential</h2>
          </div>
          <span className={`ai-status ai-status--${String(credentials.connectionStatus).toLowerCase()}`}>
            {credentials.connectionStatus}
          </span>
        </div>

        <div className="ai-form-grid ai-form-grid--two">
          <label>
            Provider
            <input
              value={credentials.provider}
              onChange={(event) => handleCredentialChange("provider", event.target.value)}
            />
          </label>
          <label>
            API Key
            <input
              type="password"
              placeholder={credentials.maskedApiKey || "Enter API key"}
              value={credentials.apiKey}
              onChange={(event) => handleCredentialChange("apiKey", event.target.value)}
            />
          </label>
        </div>

        <div className="ai-card__actions">
          <button type="button" className="secondary-button" onClick={handleTestConnection}>
            Test Connection
          </button>
          <button type="button" className="primary-button" onClick={handleSaveCredentials}>
            Save Credential
          </button>
        </div>
      </div>

      {promptMap.map((item) => (
        <div className="ai-card ai-prompt-card" key={item.id}>
          <div className="ai-card__header">
            <div>
              <span className="ai-badge">{item.key}</span>
              <h2>{item.name}</h2>
              <p>{item.description}</p>
            </div>

            <span className={`ai-status ai-status--${String(item.status).toLowerCase()}`}>
              {item.status}
            </span>
          </div>

          <div className="ai-form-grid ai-form-grid--two">
            <label>
              Model
              <select
                value={item.model}
                onChange={(event) => handlePromptChange(item.id, "model", event.target.value)}
              >
                {models.map((model) => (
                  <option key={model}>{model}</option>
                ))}
              </select>
            </label>

            <label>
              Status
              <select
                value={item.status}
                onChange={(event) => handlePromptChange(item.id, "status", event.target.value)}
              >
                {statuses.map((status) => (
                  <option key={status}>{status}</option>
                ))}
              </select>
            </label>
          </div>

          <label className="ai-textarea-label">
            Prompt Template
            <textarea
              value={item.prompt_template || ""}
              onChange={(event) => handlePromptChange(item.id, "prompt_template", event.target.value)}
            />
          </label>

          <div className="ai-card__actions">
            <button type="button" className="secondary-button" onClick={() => handleTestPromptResult(item)}>
              Test Prompt
            </button>
            <button type="button" className="primary-button" onClick={() => handleSavePrompt(item)}>
              Save Prompt
            </button>
          </div>
        </div>
      ))}

      <div className="ai-card">
        <div className="ai-card__header">
          <div>
            <span className="ai-badge">result</span>
            <h2>Latest Test Result</h2>
          </div>
        </div>
        <pre>{testResult || "Run a test to view backend response."}</pre>
      </div>
    </div>
  );
}
